import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Shield, ArrowRight, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/App";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/auth/login`, { email, password });
      login(resp.data.user, resp.data.token);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#05080e] flex" data-testid="login-page">
      {/* Left: Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/2ab261de-8e1a-49ac-9fa7-a955c72c7d05/images/c256a3eac591683e39b41b1405506c62e09617676a76e90a27f63bba418bd5be.png"
          alt="Secure operations"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#05080e] via-[#05080e]/70 to-[#05080e]/30" />
        <div className="absolute top-10 left-12 flex items-center gap-2 text-[10px] font-mono tracking-[0.28em] text-zinc-400"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 op-dot"/>OPERATIONAL</div>
        <div className="absolute bottom-14 left-12 right-12">
          <h2 className="text-2xl font-semibold tracking-tight text-zinc-50 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
            Evidence-first security, by design.
          </h2>
          <p className="text-sm text-zinc-400 leading-relaxed max-w-sm">Sign in to run bounded agents, review findings and export the evidence behind every result.</p>
        </div>
      </div>
      {/* Right: Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-10" data-testid="login-logo-link">
            <Shield className="w-7 h-7 text-cyan-300" strokeWidth={1.5} />
            <span className="text-xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>ZYBERCURE</span>
          </Link>

          <h1 className="text-2xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Welcome back</h1>
          <p className="text-sm text-zinc-400 mb-8">Sign in to your command center.</p>

          {error && (
            <div className="mb-4 p-3 rounded-sm bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-[11px] tracking-[0.18em] uppercase text-zinc-500 font-semibold">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="operator@company.com"
                className="mt-2 bg-zinc-950/60 border-white/10 focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500/50 rounded-sm h-11 text-zinc-50 placeholder:text-zinc-600 transition-colors"
                required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[11px] tracking-[0.18em] uppercase text-zinc-500 font-semibold">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="bg-zinc-950/60 border-white/10 focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500/50 rounded-sm h-11 text-zinc-50 placeholder:text-zinc-600 pr-10 transition-colors"
                  required
                  data-testid="login-password-input"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-cyan-300 hover:bg-cyan-200 text-zinc-950 h-11 rounded-sm font-semibold tracking-wide transition-colors"
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in..." : "Sign in"} {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[11px] text-zinc-600 uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <Button
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full border-white/10 text-zinc-300 hover:bg-zinc-900 hover:text-zinc-50 h-11 rounded-sm font-semibold bg-transparent transition-colors"
            data-testid="login-google-btn"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Continue with Google
          </Button>

          <p className="mt-8 text-center text-sm text-zinc-500">
            Don't have an account?{" "}
            <Link to="/register" className="text-cyan-300 hover:text-cyan-200 font-medium transition-colors" data-testid="login-register-link">
              Create account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
